import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, Settings2, Variable, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { PromptConfig } from '@/contexts/IDEContext';

export type PromptMode = 'append' | 'override' | 'variable';

interface IDEInputPanelProps {
  agentContent: string;
  userPrompt: string;
  onUserPromptChange: (prompt: string) => void;
  inputVariables: Record<string, string>;
  onVariablesChange: (variables: Record<string, string>) => void;
  promptConfig: PromptConfig;
  onPromptConfigChange: (config: PromptConfig) => void;
  disabled?: boolean;
  mode: 'oneshot' | 'chat';
}

// Detect Jinja2 template variables from content
function detectVariables(content: string): string[] {
  const pattern = /\{\{\s*([\w.]+)\s*\}\}/g;
  const matches = [...content.matchAll(pattern)];

  const variables = new Set<string>();
  for (const match of matches) {
    const fullVar = match[1];
    const rootVar = fullVar.split('.')[0];
    variables.add(rootVar);
  }

  const reserved = ['loop', 'self', 'super', 'true', 'false', 'none', 'True', 'False', 'None'];
  return [...variables].filter(v => !reserved.includes(v));
}

export function IDEInputPanel({
  agentContent,
  userPrompt,
  onUserPromptChange,
  inputVariables,
  onVariablesChange,
  promptConfig,
  onPromptConfigChange,
  disabled = false,
  mode,
}: IDEInputPanelProps) {
  const [isOpen, setIsOpen] = useState(true);

  // Detect template variables from agent content
  const detectedVariables = useMemo(() => {
    return detectVariables(agentContent);
  }, [agentContent]);

  // Variables that need manual input
  const requiredVariables = useMemo(() => {
    if (promptConfig.mode === 'variable' && promptConfig.targetVariable) {
      return detectedVariables.filter(v => v !== promptConfig.targetVariable);
    }
    return detectedVariables;
  }, [detectedVariables, promptConfig]);

  const missingVariables = requiredVariables.filter(v => !inputVariables[v]?.trim());
  const hasVariables = detectedVariables.length > 0;
  const isOneShot = mode === 'oneshot';

  // Extract body content (text after ---) to check if there's a default prompt
  const bodyContent = useMemo(() => {
    const match = agentContent.match(/^---\s*\n[\s\S]*?\n---\s*\n([\s\S]*)$/);
    return match ? match[1].trim() : '';
  }, [agentContent]);

  const handleVariableChange = useCallback((name: string, value: string) => {
    onVariablesChange({
      ...inputVariables,
      [name]: value,
    });
  }, [inputVariables, onVariablesChange]);

  const handleModeChange = useCallback((newMode: PromptMode) => {
    if (newMode === 'variable' && detectedVariables.length > 0) {
      onPromptConfigChange({
        mode: newMode,
        targetVariable: promptConfig.targetVariable || detectedVariables[0],
      });
    } else {
      onPromptConfigChange({ mode: newMode });
    }
  }, [detectedVariables, promptConfig.targetVariable, onPromptConfigChange]);

  const handleTargetVariableChange = useCallback((variable: string) => {
    onPromptConfigChange({
      mode: 'variable',
      targetVariable: variable,
    });
  }, [onPromptConfigChange]);

  // Count issues for badge
  // User prompt is only "missing" if BOTH body content AND user prompt are empty
  const hasPromptContent = bodyContent || userPrompt.trim();
  const issueCount = missingVariables.length + (isOneShot && !hasPromptContent ? 1 : 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border-b bg-muted/30">
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Run Configuration</span>
              {hasVariables && (
                <Badge variant="secondary" className="text-xs">
                  <Variable className="h-3 w-3 mr-1" />
                  {detectedVariables.length} var{detectedVariables.length !== 1 ? 's' : ''}
                </Badge>
              )}
              {issueCount > 0 && (
                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {issueCount} missing
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            {/* Prompt Mode Selector - only for one-shot mode */}
            {isOneShot && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  <Label className="text-sm font-medium">Prompt Mode</Label>
                </div>
                <RadioGroup
                  value={promptConfig.mode}
                  onValueChange={(v) => handleModeChange(v as PromptMode)}
                  className="flex flex-wrap gap-4"
                  disabled={disabled}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="append" id="mode-append" />
                    <Label htmlFor="mode-append" className="text-sm font-normal cursor-pointer">
                      Append to body
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="override" id="mode-override" />
                    <Label htmlFor="mode-override" className="text-sm font-normal cursor-pointer">
                      Override body
                    </Label>
                  </div>
                  {hasVariables && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="variable" id="mode-variable" />
                      <Label htmlFor="mode-variable" className="text-sm font-normal cursor-pointer flex items-center gap-2">
                        Fill variable:
                        {promptConfig.mode === 'variable' && (
                          <Select
                            value={promptConfig.targetVariable}
                            onValueChange={handleTargetVariableChange}
                            disabled={disabled}
                          >
                            <SelectTrigger className="h-7 w-28 text-xs">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {detectedVariables.map((v) => (
                                <SelectItem key={v} value={v} className="text-xs font-mono">
                                  {`{{ ${v} }}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              </div>
            )}

            {/* Template Variables */}
            {requiredVariables.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Variable className="h-4 w-4 text-purple-500" />
                  <Label className="text-sm font-medium">Template Variables</Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {requiredVariables.map((variable) => {
                    const isMissing = !inputVariables[variable]?.trim();
                    return (
                      <div key={variable} className="space-y-1">
                        <Label
                          className={cn(
                            "text-xs font-mono",
                            isMissing ? "text-orange-600" : "text-muted-foreground"
                          )}
                        >
                          {"{{ "}{variable}{" }}"}
                          {isMissing && <span className="text-orange-500 ml-1">*</span>}
                        </Label>
                        <Input
                          value={inputVariables[variable] || ''}
                          onChange={(e) => handleVariableChange(variable, e.target.value)}
                          placeholder={`Value for ${variable}`}
                          className={cn(
                            "h-8 text-sm",
                            isMissing && "border-orange-300 focus-visible:ring-orange-400"
                          )}
                          disabled={disabled}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* User Prompt - only for one-shot mode */}
            {isOneShot && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">User Prompt</Label>
                <Textarea
                  value={userPrompt}
                  onChange={(e) => onUserPromptChange(e.target.value)}
                  placeholder={
                    promptConfig.mode === 'variable' && promptConfig.targetVariable
                      ? `Enter value for {{ ${promptConfig.targetVariable} }}...`
                      : "Enter the prompt to send to the agent..."
                  }
                  className={cn(
                    "min-h-20 resize-none text-sm",
                    !hasPromptContent && "border-orange-300"
                  )}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">
                  {promptConfig.mode === 'append' && (
                    bodyContent
                      ? 'This will be appended after the agent body content. Leave empty to use body content only.'
                      : 'Enter the message to send to the agent.'
                  )}
                  {promptConfig.mode === 'override' && 'This will replace the agent prompt field.'}
                  {promptConfig.mode === 'variable' && promptConfig.targetVariable &&
                    `This will fill the {{ ${promptConfig.targetVariable} }} variable.`}
                </p>
              </div>
            )}

            {/* Chat mode note */}
            {!isOneShot && (
              <p className="text-sm text-muted-foreground italic">
                In interactive mode, use the chat panel to send messages to the agent.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default IDEInputPanel;
